'use client';

import { c as _c } from "react/compiler-runtime";
import { Box, Button, IconButton, Stack, Switch, Typography } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useRef, useState } from 'react';
import { FormTextField } from '@/components/form/formTextField';
import type { FieldValidation } from '@/components/form/validation/fieldValidation';
import { Tag } from '../tag';
interface TagRow {
  id: string;
  key: string;
  value: string;
}
const areTagRowsValid = (rows: TagRow[]) => rows.every((row, index) => row.key.trim() !== '' && rows.findIndex(other => other.key === row.key) === index);

// A stored record cannot hold a repeated key. An empty key can, and the step remounts with it.
export const arePersistedTagKeysValid = (tags: Record<string, string> | undefined, keepOriginalTags: boolean) => keepOriginalTags || Object.keys(tags ?? {}).every(key => key.trim() !== '');
const rowsToRecord = (rows: TagRow[]): Record<string, string> => {
  const record: Record<string, string> = {};
  for (const row of rows) {
    record[row.key] = row.value;
  }
  return record;
};
const recordToRows = (tags: Record<string, string> | undefined): TagRow[] => Object.entries(tags ?? {}).map(([key, value], index) => ({
  id: `tag-${index}-${key}`,
  key,
  value
}));
const keyValidation = (row: TagRow, rows: TagRow[]): FieldValidation => ({
  validateInput: value => {
    if (value.trim() === '') {
      return false;
    }
    return !rows.some(other => other.id !== row.id && other.key === value);
  },
  errorMessage: value => {
    if (value.trim() === '') {
      return 'Key is required';
    }
    if (rows.some(other => other.id !== row.id && other.key === value)) {
      return 'Key must be unique';
    }
    return '';
  }
});
const TagEntryFields = t0 => {
  const $ = _c(24);
  const {
    row,
    rows,
    onKeyChange,
    onValueChange,
    onRemove
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: "24px",
      alignItems: "end",
      width: "560px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      flex: "1"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = row.key;
  let t4;
  if ($[2] !== row || $[3] !== rows) {
    t4 = keyValidation(row, rows);
    $[2] = row;
    $[3] = rows;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== onKeyChange) {
    t5 = event => onKeyChange(event.target.value);
    $[5] = onKeyChange;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== row.key || $[8] !== t4 || $[9] !== t5) {
    t6 = <FormTextField sx={t2} size="small" title="Key" placeholder="" value={t3} validation={t4} onChange={t5} />;
    $[7] = row.key;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      flex: "1"
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== onValueChange) {
    t8 = event_0 => onValueChange(event_0.target.value);
    $[12] = onValueChange;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== row.value || $[15] !== t8) {
    t9 = <FormTextField sx={t7} size="small" title="Value" placeholder="" value={row.value} onChange={t8} />;
    $[14] = row.value;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <i className="material-symbols-close-rounded" />;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== onRemove) {
    t11 = <IconButton onClick={onRemove} aria-label="Remove tag">{t10}</IconButton>;
    $[18] = onRemove;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t6 || $[22] !== t9) {
    t12 = <Stack direction="row" sx={t1}>{t6}{t9}{t11}</Stack>;
    $[20] = t11;
    $[21] = t6;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
};
export const TagsSection = t0 => {
  const $ = _c(32);
  const {
    initialTags,
    tags: t1,
    keepOriginalTags,
    setKeepOriginalTags,
    onChange,
    onValidityChange,
    provider
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const tags = t2;
  let t3;
  if ($[2] !== tags) {
    t3 = JSON.stringify(tags ?? {});
    $[2] = tags;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const serializedTags = t3;
  let t4;
  if ($[4] !== tags) {
    t4 = () => recordToRows(tags);
    $[4] = tags;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [rows, setRows] = useState(t4);
  const [appliedTags, setAppliedTags] = useState(serializedTags);
  const nextRowId = useRef(0);
  if (serializedTags !== appliedTags) {
    setAppliedTags(serializedTags);
    setRows(recordToRows(tags));
  }
  let t5;
  if ($[6] !== onValidityChange) {
    t5 = (nextRows, nextKeepOriginalTags) => {
      onValidityChange?.(nextKeepOriginalTags || areTagRowsValid(nextRows));
    };
    $[6] = onValidityChange;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const reportValidity = t5;
  let t6;
  if ($[8] !== keepOriginalTags || $[9] !== onChange || $[10] !== reportValidity) {
    t6 = nextRows_0 => {
      const record = rowsToRecord(nextRows_0);
      setAppliedTags(JSON.stringify(record));
      setRows(nextRows_0);
      onChange(record);
      reportValidity(nextRows_0, keepOriginalTags);
    };
    $[8] = keepOriginalTags;
    $[9] = onChange;
    $[10] = reportValidity;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const emitRows = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      nextRowId.current = nextRowId.current + 1;
      return `tag-new-${nextRowId.current}`;
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const createRowId = t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      gap: "24px"
    };
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== keepOriginalTags || $[15] !== provider || $[16] !== reportValidity || $[17] !== rows || $[18] !== setKeepOriginalTags) {
    t9 = setKeepOriginalTags && <Box className="flex justify-between items-center"><Typography>{`Keep original ${provider === Provider.Gcp ? "labels" : "tags"}`}</Typography><Switch size="small" checked={keepOriginalTags} onChange={() => {
        const nextKeepOriginalTags_0 = !keepOriginalTags;
        setKeepOriginalTags(nextKeepOriginalTags_0);
        reportValidity(rows, nextKeepOriginalTags_0);
      }} /></Box>;
    $[14] = keepOriginalTags;
    $[15] = provider;
    $[16] = reportValidity;
    $[17] = rows;
    $[18] = setKeepOriginalTags;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== initialTags || $[21] !== keepOriginalTags) {
    t10 = keepOriginalTags && initialTags && Object.keys(initialTags).length > 0 && <Stack className="w-full" direction="row" sx={{
      gap: "8px",
      flexWrap: "wrap"
    }}>{Object.entries(initialTags).map(_temp2)}</Stack>;
    $[20] = initialTags;
    $[21] = keepOriginalTags;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== emitRows || $[24] !== keepOriginalTags || $[25] !== provider || $[26] !== rows) {
    t11 = !keepOriginalTags && <Stack sx={{
      gap: "40px"
    }}>{rows.length > 0 && <Stack sx={{
        gap: "24px"
      }}>{rows.map(row => <TagEntryFields key={row.id} row={row} rows={rows} onKeyChange={key => emitRows(rows.map(current => current.id === row.id ? {
          ...current,
          key
        } : current))} onValueChange={value => emitRows(rows.map(current_0 => current_0.id === row.id ? {
          ...current_0,
          value
        } : current_0))} onRemove={() => emitRows(rows.filter(current_1 => current_1.id !== row.id))} />)}</Stack>}<Button sx={{
        width: "max-content"
      }} variant="outlined" onClick={() => emitRows([...rows, {
        id: createRowId(),
        key: "",
        value: ""
      }])}>{`+ Add ${provider === Provider.Gcp ? "Label" : "Tag"}`}</Button></Stack>;
    $[23] = emitRows;
    $[24] = keepOriginalTags;
    $[25] = provider;
    $[26] = rows;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t9) {
    t12 = <Stack className="w-full px-[40px]" sx={t8}>{t9}{t10}{t11}</Stack>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t9;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
};
function _temp(x) {
  return x;
}
function _temp2(entry) {
  return <Tag variant="secondary" key={entry[0]} content={entry.filter(_temp).join("=")} />;
}